import { Box, Typography } from '@material-ui/core'
import { InfoOutlined } from '@material-ui/icons'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import DateRangeOutlinedIcon from '@material-ui/icons/DateRangeOutlined'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import AlertDialog from '@/components/alert-dialog'
import Button from '@/components/button'
import View from '@/components/view'
import { InterestLayers, layersMap } from '@/constants/diagnoses'
import { useGetPhysiologicalDiagnoses } from '@/hooks/queries/use-get-physiological-diagnoses'
import { useGetPhysiologicalDiagnosesMap } from '@/hooks/queries/use-get-physiological-diagnoses-map'
import { useGetPhysiologicalDiagnosisById } from '@/hooks/queries/use-get-physiological-diagnosis-by-id'
import { GeoJsonMap } from '@/pages/diagnosis-view/components/geo-json-map'
import { Header } from '@/pages/diagnosis-view/components/header'
import Info from '@/pages/diagnosis-view/components/info'
import { LayerSelect } from '@/pages/diagnosis-view/components/layer-select'
import Legend from '@/pages/diagnosis-view/components/legend'
import Results from '@/pages/diagnosis-view/components/results'
import { Shimmer } from '@/pages/diagnosis-view/components/shimmer'

import { useStyles } from './styles'

type AlertReturn = Omit<React.ComponentProps<typeof AlertDialog>, 'open'>

type SoybeanProps = { id: string; selectedLayer: InterestLayers }

export const Soybean = ({ id, selectedLayer }: SoybeanProps) => {
  const classes = useStyles()

  const navigate = useNavigate()

  const [isCompareView, setIsCompareView] = useState(false)
  const [compareDiagnosisId, setCompareDiagnosisId] = useState<string | null>(
    null,
  )

  const [isAlertOpen, setIsAlertOpen] = useState(false)

  const { data: diagnosis } = useGetPhysiologicalDiagnosisById(id, {
    onError: () => {
      setIsAlertOpen(true)
    },
  })
  const { data: compareDiagnosis } = useGetPhysiologicalDiagnosisById(
    compareDiagnosisId!,
    {
      enabled: !!compareDiagnosisId,
      onError: () => {
        setIsAlertOpen(true)
      },
    },
  )

  const getDiagnosesQuery = useGetPhysiologicalDiagnoses(
    {
      layer: selectedLayer,
      cicle: diagnosis?.cicle,
      projectId: diagnosis?.project.id,
    },
    {
      enabled: isCompareView,
      onError: () => {
        setIsAlertOpen(true)
      },
      idForFilter: diagnosis?.id,
    },
  )
  const physiologicalMap = useGetPhysiologicalDiagnosesMap(id, {
    onError: () => {
      setIsAlertOpen(true)
    },
  })
  const comparePhysiologicalMap = useGetPhysiologicalDiagnosesMap(
    compareDiagnosisId!,
    {
      onError: () => {
        setIsAlertOpen(true)
      },
      enabled: !!compareDiagnosisId,
    },
  )

  function getBorderColor() {
    return physiologicalMap.data?.fields.features.reduce((acc, feature) => {
      acc[feature.properties.field] = feature.properties.color
      return acc
    }, {} as Record<string, string>)
  }

  const getAlertProps = (): AlertReturn => {
    return {
      icon: <InfoOutlined />,
      title: 'Ocorreu um erro',
      subtitle: 'Não conseguimos carregar o diagnóstico selecionado.',
      items: ['Tente novamente mais tarde ou entre em contato com a Cromai.'],
      variant: 'error' as const,
      confirm: 'Ok, entendi',
      handleConfirm: () => {
        if (isCompareView) {
          setIsCompareView(false)
          setCompareDiagnosisId(null)
          return setIsAlertOpen(false)
        }
        navigate(-1)
      },
    }
  }

  return (
    <>
      {isCompareView && physiologicalMap.data && diagnosis ? (
        <View
          variant='compare'
          padding='XL'
          overflow
          aside={
            <>
              <Header
                title='Análise temporal'
                goBack={() => {
                  setIsCompareView(false)
                  setCompareDiagnosisId(null)
                }}
              />

              <Results
                selectedLayer={selectedLayer}
                diagnosis={diagnosis}
                borderColor={getBorderColor()!}
                minInfo
                hasTitle
              />

              <Results
                minInfo
                diagnosis={compareDiagnosis}
                borderColor={getBorderColor()!}
                selectedLayer={selectedLayer}
              />

              <Legend
                title={layersMap[selectedLayer].message}
                values={{
                  max: Number(
                    diagnosis?.summary?.totals[selectedLayer]?.max.toFixed(2),
                  ),
                  min: Number(
                    diagnosis?.summary?.totals[selectedLayer]?.min.toFixed(2),
                  ),
                }}
                colors={layersMap[selectedLayer].colorMap}
                unitOfMeasurement={layersMap[selectedLayer].measurement}
              />
            </>
          }
          leftNode={
            <>
              <LayerSelect
                disabled
                renderDisabledItem={{
                  id: diagnosis.id,
                  date: diagnosis.mosaic.date,
                  phenologicalStage: diagnosis.phenological_stage,
                }}
              />

              <GeoJsonMap
                disabledControls
                geoJsonBounds={physiologicalMap.data.geoJsonBounds?.pad(-1)}
                data={physiologicalMap.data}
                selectedLayer={selectedLayer}
                measurement={layersMap[selectedLayer].measurement}
                title={layersMap[selectedLayer].title}
                message={layersMap[selectedLayer].helpText}
              />
            </>
          }
          rightNode={
            <>
              {!getDiagnosesQuery.data ? (
                <Shimmer />
              ) : (
                <>
                  <LayerSelect
                    items={getDiagnosesQuery.data.map((item) => ({
                      id: item.id,
                      date: item.mosaic.date,
                      phenologicalStage: item.phenological_stage,
                    }))}
                    onChange={(e) => {
                      const diagnosis = getDiagnosesQuery.data.find(
                        (item) => item.id === e.target.value,
                      )
                      if (diagnosis) setCompareDiagnosisId(diagnosis.id)
                    }}
                  />

                  {comparePhysiologicalMap.isFetching ? (
                    <Shimmer />
                  ) : comparePhysiologicalMap.data ? (
                    <GeoJsonMap
                      disabledControls
                      geoJsonBounds={comparePhysiologicalMap.data.geoJsonBounds?.pad(
                        -1,
                      )}
                      data={comparePhysiologicalMap.data}
                      selectedLayer={selectedLayer}
                      measurement={layersMap[selectedLayer].measurement}
                      title={layersMap[selectedLayer].title}
                      message={layersMap[selectedLayer].helpText}
                    />
                  ) : (
                    <Box
                      height='100%'
                      bgcolor='#F9F9F9'
                      border='3px dashed #DDDDDD'
                      textAlign='center'
                      padding='1rem'
                    >
                      <Box width='25rem' margin='8rem auto' color='#00658B'>
                        <ArrowUpwardIcon style={{ fontSize: '7rem' }} />
                        <Typography
                          variant='h3'
                          color='inherit'
                          style={{ marginTop: '3rem' }}
                        >
                          Selecione a data do diagnóstico para realizar a
                          análise temporal.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </>
          }
        />
      ) : (
        <View
          aside={
            <>
              <Header
                title='Desempenho fisiológico'
                goBack={() => navigate(-1)}
              />

              <Button
                outlined
                className={classes.button}
                disabled={!physiologicalMap.data}
                onClick={() => setIsCompareView(true)}
              >
                <DateRangeOutlinedIcon /> Visualizar análise temporal
              </Button>

              {!physiologicalMap.data || !diagnosis ? (
                <Shimmer variant='box' length={3} maxSizeBox='16rem' />
              ) : (
                <>
                  <Info
                    icon={layersMap[selectedLayer].icon}
                    title={layersMap[selectedLayer].message}
                    project={diagnosis?.project.name}
                    state={diagnosis?.state_acr}
                    cicle={diagnosis?.cicle}
                    date={diagnosis?.concluded_at}
                    phenologicalStage={diagnosis?.phenological_stage}
                  />

                  <Results
                    diagnosis={diagnosis}
                    selectedLayer={selectedLayer}
                    hasTitle={isCompareView}
                    borderColor={getBorderColor()!}
                  />
                  <Legend
                    title={layersMap[selectedLayer].message}
                    colors={layersMap[selectedLayer].colorMap}
                    unitOfMeasurement={layersMap[selectedLayer].measurement}
                    values={{
                      max: Number(
                        diagnosis?.summary?.totals[selectedLayer]?.max.toFixed(
                          2,
                        ),
                      ),
                      min: Number(
                        diagnosis?.summary?.totals[selectedLayer]?.min.toFixed(
                          2,
                        ),
                      ),
                    }}
                  />
                </>
              )}
            </>
          }
          section={
            !physiologicalMap.data ? (
              <Shimmer />
            ) : (
              <GeoJsonMap
                geoJsonBounds={physiologicalMap.data.geoJsonBounds}
                data={physiologicalMap.data}
                selectedLayer={selectedLayer}
                measurement={layersMap[selectedLayer].measurement}
                title={layersMap[selectedLayer].title}
                message={layersMap[selectedLayer].helpText}
              />
            )
          }
        />
      )}

      <AlertDialog {...getAlertProps()} open={isAlertOpen} />
    </>
  )
}
